(() => {
  const HTML = document.documentElement;
  const REDUCED_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MOTION_SELECTOR = '[data-mlt-motion]:not([data-mlt-v3-bound])';
  const LOCAL_TIME_SELECTOR = '[data-mlt-local-time]:not([data-mlt-time-bound])';
  const SEAL_SELECTOR = '[data-mlt-scroll-seal]:not([data-mlt-seal-bound])';

  const state = {
    observers: new Set(),
    timeTimer: null,
    reduced: REDUCED_QUERY.matches
  };

  const safeSessionGet = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const safeSessionSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage may be unavailable in privacy mode. Animation still works.
    }
  };

  const createMotionKey = (element) => {
    const explicit = element.getAttribute('data-mlt-key');
    if (explicit) return `mlt:v3:${explicit}`;

    const section = element.closest('[id]');
    const sectionId = section?.id || 'page';
    const index = Array.from(
      document.querySelectorAll('[data-mlt-motion]')
    ).indexOf(element);

    return `mlt:v3:${sectionId}:${element.dataset.mltMotion}:${index}`;
  };

  const activate = (element) => {
    if (!element || element.classList.contains('is-mlt-active')) return;

    const onceMode = element.getAttribute('data-mlt-once');

    if (onceMode === 'session') {
      safeSessionSet(createMotionKey(element), '1');
    }

    if (element.dataset.mltMotion === 'count') {
      animateCount(element);
    }

    element.classList.add('is-mlt-active');
    element.dispatchEvent(
      new CustomEvent('mlt:motion:active', {
        bubbles: true,
        detail: { motion: element.dataset.mltMotion }
      })
    );
  };

  const setStatic = (element) => {
    element.classList.add('is-mlt-active');
    element.removeAttribute('aria-hidden');

    if (element.dataset.mltMotion === 'count') {
      const target = element.getAttribute('data-mlt-count-to');
      if (target !== null) element.textContent = target;
    }
  };

  const animateCount = (element) => {
    if (element.dataset.mltCounted === 'true') return;
    element.dataset.mltCounted = 'true';

    const rawTarget = element.getAttribute('data-mlt-count-to');
    const target = Number.parseFloat(rawTarget);
    if (!Number.isFinite(target)) return;

    const rawStart = element.getAttribute('data-mlt-count-from');
    const start = Number.isFinite(Number.parseFloat(rawStart))
      ? Number.parseFloat(rawStart)
      : 0;

    const decimals = Math.max(
      0,
      Number.parseInt(element.getAttribute('data-mlt-count-decimals') || '0', 10)
    );

    const prefix = element.getAttribute('data-mlt-count-prefix') || '';
    const suffix = element.getAttribute('data-mlt-count-suffix') || '';
    const duration = Number.parseInt(
      element.getAttribute('data-mlt-count-duration') || '520',
      10
    );

    if (state.reduced || duration <= 0) {
      element.textContent = `${prefix}${target.toFixed(decimals)}${suffix}`;
      return;
    }

    const startTime = performance.now();
    const easeOut = (value) => 1 - Math.pow(1 - value, 3);

    const frame = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const value = start + (target - start) * easeOut(progress);
      element.textContent = `${prefix}${value.toFixed(decimals)}${suffix}`;

      if (progress < 1) {
        requestAnimationFrame(frame);
      }
    };

    requestAnimationFrame(frame);
  };

  const bindMotion = (scope = document) => {
    const elements = Array.from(scope.querySelectorAll(MOTION_SELECTOR));
    if (!elements.length) return;

    elements.forEach((element) => {
      element.dataset.mltV3Bound = 'true';

      const onceMode = element.getAttribute('data-mlt-once');
      const alreadyPlayed =
        onceMode === 'session' &&
        safeSessionGet(createMotionKey(element)) === '1';

      if (
        state.reduced ||
        alreadyPlayed ||
        !('IntersectionObserver' in window)
      ) {
        setStatic(element);
        return;
      }

      const threshold = Number.parseFloat(
        element.getAttribute('data-mlt-threshold') || '0.16'
      );

      const observer = new IntersectionObserver(
        (entries, activeObserver) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            activate(entry.target);
            activeObserver.unobserve(entry.target);
          });
        },
        {
          threshold: Number.isFinite(threshold) ? threshold : 0.16,
          rootMargin:
            element.getAttribute('data-mlt-root-margin') ||
            '0px 0px -7% 0px'
        }
      );

      observer.observe(element);
      state.observers.add(observer);
    });
  };

  const updateLocalTimes = () => {
    document.querySelectorAll('[data-mlt-local-time]').forEach((element) => {
      const now = new Date();
      const includeSeconds =
        element.getAttribute('data-mlt-local-time') === 'seconds';

      const formatter = new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: includeSeconds ? '2-digit' : undefined,
        hour12: false
      });

      element.textContent = formatter.format(now);
      element.setAttribute(
        'datetime',
        now.toISOString()
      );
    });
  };

  const bindLocalTime = (scope = document) => {
    const elements = scope.querySelectorAll(LOCAL_TIME_SELECTOR);
    if (!elements.length) return;

    elements.forEach((element) => {
      element.dataset.mltTimeBound = 'true';
    });

    updateLocalTimes();

    if (!state.timeTimer) {
      state.timeTimer = window.setInterval(updateLocalTimes, 1000);
    }
  };

  const bindScrollSeal = (scope = document) => {
    scope.querySelectorAll(SEAL_SELECTOR).forEach((element) => {
      element.dataset.mltSealBound = 'true';

      const threshold = Number.parseInt(
        element.getAttribute('data-mlt-scroll-seal') || '40',
        10
      );

      let ticking = false;

      const update = () => {
        element.classList.toggle(
          'is-sealed',
          window.scrollY > threshold
        );
        ticking = false;
      };

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      update();

      element.__mltSealCleanup = () => {
        window.removeEventListener('scroll', onScroll);
      };
    });
  };

  const prepareVideos = (scope = document) => {
    scope.querySelectorAll('[data-mlt-video]').forEach((video) => {
      if (video.dataset.mltVideoBound === 'true') return;
      video.dataset.mltVideoBound = 'true';

      video.muted = true;
      video.playsInline = true;

      if (state.reduced) {
        video.pause();
        video.removeAttribute('autoplay');
        return;
      }

      if (
        video.hasAttribute('autoplay') &&
        typeof video.play === 'function'
      ) {
        const playPromise = video.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {
            video.dataset.mltAutoplayBlocked = 'true';
          });
        }
      }
    });
  };

  const init = (scope = document) => {
    HTML.classList.add('mlt-v3-ready');
    bindMotion(scope);
    bindLocalTime(scope);
    bindScrollSeal(scope);
    prepareVideos(scope);
  };

  const handleReducedMotionChange = (event) => {
    state.reduced = event.matches;

    if (state.reduced) {
      document.querySelectorAll('[data-mlt-motion]').forEach(setStatic);
      document.querySelectorAll('[data-mlt-video]').forEach((video) => {
        video.pause();
        video.removeAttribute('autoplay');
      });
    }
  };

  if (typeof REDUCED_QUERY.addEventListener === 'function') {
    REDUCED_QUERY.addEventListener('change', handleReducedMotionChange);
  } else {
    REDUCED_QUERY.addListener(handleReducedMotionChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target
      .querySelectorAll('[data-mlt-scroll-seal]')
      .forEach((element) => {
        if (typeof element.__mltSealCleanup === 'function') {
          element.__mltSealCleanup();
        }
      });
  });
})();
